import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClipboardCheck, ArrowRight, X } from 'lucide-react';
import api from '@/services/api';

/**
 * Slim navy bar pinned under the top bar (ClearDesk notice style) reminding a person
 * to finish their staff appraisal. Shown only while something is left to do;
 * closing it hides it until the next day.
 */
const CLOSED_KEY = 'puffin.appraisalBar.closed';
const today = () => new Date().toISOString().slice(0, 10);
const closedToday = () => {
  try { return localStorage.getItem(CLOSED_KEY) === today(); } catch { return false; }
};

const AppraisalNoticeBar = ({ isSidebarCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [closed, setClosed] = useState(closedToday);
  const onPage = location.pathname.startsWith('/self-service/appraisal');

  // Look again after leaving the appraisal page, so the bar goes once it's done
  useEffect(() => {
    if (closed || onPage) return;
    api.get('/appraisals/me').then((r) => setMe(r.data?.data)).catch(() => setMe(null));
  }, [closed, onPage]);

  if (closed || onPage || !me) return null;
  const reviewLeft = me.appraisal?.status !== 'submitted';
  const feedbackLeft = !me.anonymousDone;
  if (!reviewLeft && !feedbackLeft) return null;

  const left = reviewLeft && feedbackLeft
    ? 'Please complete your end-of-year review and the anonymous feedback.'
    : reviewLeft
      ? 'Your anonymous feedback is in. Please submit your end-of-year review.'
      : 'Your review is in. Please send the anonymous feedback too.';

  const close = () => {
    try { localStorage.setItem(CLOSED_KEY, today()); } catch { /* ignore */ }
    setClosed(true);
  };

  return (
    <>
    {/* Keeps the page clear of the pinned bar */}
    <div className="-mt-6 mb-6 h-10" aria-hidden="true" />
    <div
      className="fixed top-16 right-0 z-30 bg-[#16314C] transition-all duration-300"
      style={{ left: isSidebarCollapsed ? '64px' : '256px' }}
      role="status"
    >
      <div className="flex h-10 items-center gap-2.5 px-4 sm:px-6">
        <ClipboardCheck size={15} strokeWidth={2} className="flex-shrink-0 text-white/80" aria-hidden="true" />
        <span className="hidden h-[18px] flex-shrink-0 items-center rounded-[5px] bg-white/15 px-1.5 text-[11px] font-medium text-white sm:inline-flex">Action needed</span>
        <p className="min-w-0 flex-1 truncate text-[13px] leading-snug">
          <span className="font-semibold text-white">Staff appraisal {me.year}</span>
          <span className="ml-1 text-white/70 sm:ml-1.5">{left}</span>
        </p>
        <button
          type="button"
          onClick={() => navigate('/self-service/appraisal')}
          className="group inline-flex h-7 flex-shrink-0 items-center gap-1 text-[12.5px] font-semibold text-white transition-colors hover:text-white/80"
        >
          Open appraisal<ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
        </button>
        <button
          type="button"
          onClick={close}
          aria-label="Hide until tomorrow"
          title="Hide until tomorrow"
          className="-mr-1.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X size={14} />
        </button>
      </div>
    </div>
    </>
  );
};

export default AppraisalNoticeBar;
